import { useState } from 'react';
import { pedir } from '../lib/api';
import { idioma, tx } from '../i18n';
import { vivo } from '../i18n/vivo';
import { bilingue, permisos } from '../lib/appliance';
import { useConsulta } from '../lib/consulta';
import { cuando } from '../lib/formato';
import { useSesion } from '../sesion/sesion';
import { Consulta, Pastilla, Tabla, Vacio, Ventana as Modal } from '../ui/piezas';

interface VersionFila {
  numero: string;
  canal: string;
  notas_en: string;
  notas_es: string;
  paquete: string | null;
  minima_desde: string | null;
  retirada: string | null;
  /**
   * Si se le puede pedir a un appliance: hay paquete, anotado o **en el blob**. Mirar
   * `paquete` a secas dejaba esta pantalla sin ninguna versión disponible desde que el
   * paquete se busca solo en el blob y esa columna va vacía. Opcional: una API más vieja
   * no manda el campo, y entonces manda lo que hay.
   */
  aplicable?: boolean;
}

interface Orden {
  id: string;
  que: string;
  parametros: { version?: string };
  estado: 'pendiente' | 'entregada' | 'hecha' | 'fallo';
  quien: string;
  creada: string;
  terminada: string | null;
  detalle: { en?: string; es?: string } | null;
}

interface VentanaMant {
  dias: number[];
  desde: string;
  hasta: string;
}

/** `vivo`: los días siguen al idioma aunque esto sea una constante de módulo. */
const DIAS: string[] = vivo(() => tx.actualizar.diasCortos);
const TONO: Record<Orden['estado'], 'arriba' | 'parcial' | 'caido' | 'desconocido'> = {
  hecha: 'arriba',
  entregada: 'parcial',
  pendiente: 'desconocido',
  fallo: 'caido',
};
/** `vivo`: los estados de una orden siguen al idioma. */
const DICHO: Record<string, string> = vivo(() => tx.actualizar.estados);

/**
 * Actualizar **este** appliance (docs/centro.md §5): a qué versión, cuándo se deja tocar y
 * cómo terminó lo último que se le pidió. El Centro no instala nada: manda la orden.
 */
/** 1.4.2 contra 1.10.0, por número y no por texto. */
function comparar(a: string, b: string): number {
  const x = a.split('.').map(Number);
  const y = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) if ((x[i] ?? 0) !== (y[i] ?? 0)) return (x[i] ?? 0) - (y[i] ?? 0);
  return 0;
}

export function Actualizar({
  applianceId,
  version,
  canal,
  ventana,
  alCambiar,
}: {
  applianceId: string;
  version: string | null;
  /** Su canal: el estable solo ve las estables; el beta, todas. */
  canal: string;
  ventana: VentanaMant | null;
  alCambiar: (aviso: string) => void;
}) {
  const { usuario } = useSesion();
  const puede = permisos(usuario?.rol);
  // con una orden en vuelo se mira cada 3 s: se ve por dónde va (bajando, respaldando, instalando…)
  const ordenes = useConsulta<Orden[]>(`/appliances/${applianceId}/ordenes`, {
    refetchInterval: (q) => ((q.state.data ?? []).some((o) => o.estado === 'pendiente' || o.estado === 'entregada') ? 3_000 : 30_000),
  });
  const versiones = useConsulta<VersionFila[]>('/versiones');
  const [pedirlo, setPedirlo] = useState(false);
  const [elegida, setElegida] = useState('');
  const [editarVentana, setEditarVentana] = useState(false);

  // solo hacia adelante: bajar de versión el Centro no lo deja (y no se ofrece)
  const disponibles = (versiones.data ?? []).filter(
    (v) => !v.retirada && (v.aplicable ?? !!v.paquete) && (!version || comparar(v.numero, version) > 0),
  );
  const enVuelo = (ordenes.data ?? []).find((o) => o.estado === 'pendiente' || o.estado === 'entregada');
  // la versión nueva para este appliance: la más nueva de su canal, mayor que la suya
  const nueva = disponibles.filter((v) => canal === 'beta' || v.canal === 'estable').sort((a, b) => comparar(b.numero, a.numero))[0];
  // otra que no sea la de arriba (una beta para un estable): recién ahí hace falta elegir
  const otras = disponibles.filter((v) => v.numero !== nueva?.numero);
  const notasNueva = nueva ? (idioma() === 'es' ? nueva.notas_es : nueva.notas_en) || nueva.notas_en || nueva.notas_es : '';

  return (
    <section className="panel">
      <h2 className="rotulo">
        {tx.actualizar.titulo}
        <span className="aux">{version ? tx.actualizar.enLaVersion(version) : tx.actualizar.versionNoReportada}</span>
      </h2>

      {nueva && !enVuelo && (
        <div className="act-nueva">
          <div className="act-nueva-cab">
            <b>{tx.actualizar.hayNueva(nueva.numero)}</b>
            {puede.actualizar && (
              <button
                type="button"
                className="btn prim"
                onClick={() => {
                  setElegida(nueva.numero);
                  setPedirlo(true);
                }}
              >
                {tx.actualizar.actualizarA(nueva.numero)}
              </button>
            )}
          </div>
          {notasNueva && (
            <div className="act-nueva-notas">
              <small>{tx.actualizar.queTrae}</small>
              <p>{notasNueva}</p>
            </div>
          )}
        </div>
      )}

      <dl className="ficha">
        <div className="dato">
          <dt>{tx.actualizar.ventana}</dt>
          <dd>
            {ventana
              ? tx.actualizar.horaDelAppliance(
                  `${ventana.dias.length ? ventana.dias.map((d) => DIAS[d]).join(', ') : tx.actualizar.cualquierDia} · ${ventana.desde}–${ventana.hasta}`,
                )
              : tx.actualizar.sinVentana}
          </dd>
        </div>
      </dl>

      <div className="botones">
        {puede.actualizar && (
          <>
            {/* la de arriba se pide con su botón; este queda solo si hay otra para elegir */}
            {otras.length > 0 && (
              <button
                type="button"
                className={nueva ? 'btn' : 'btn prim'}
                disabled={!!enVuelo}
                onClick={() => {
                  setElegida('');
                  setPedirlo(true);
                }}
              >
                {nueva ? tx.actualizar.otraVersion : tx.actualizar.actualizarEste}
              </button>
            )}
            <button type="button" className="btn" onClick={() => setEditarVentana(true)}>
              {tx.actualizar.cambiarVentana}
            </button>
          </>
        )}
        {enVuelo && <span className="nota">{tx.actualizar.yaHayUna}</span>}
        {!enVuelo && disponibles.length === 0 && (
          <span className="nota al-dia">
            <span className="punto-ok" aria-hidden /> {tx.actualizar.alDia(version ?? '—')}
          </span>
        )}
      </div>

      <Consulta q={ordenes}>
        {(ordenes.data ?? []).length === 0 ? (
          <Vacio>{tx.actualizar.nadaPedido}</Vacio>
        ) : (
          <>
            <h3 className="sub-rotulo">{tx.actualizar.loPedido}</h3>
            <Tabla>
              <thead>
                <tr>
                  <th>{tx.actualizar.cuando}</th>
                  <th>{tx.actualizar.que}</th>
                  <th>{tx.comun.estado}</th>
                  <th>{tx.actualizar.comoFue}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(ordenes.data ?? []).map((o) => (
                  <tr key={o.id}>
                    <td>
                      {cuando(o.creada)}
                      <small> · {o.quien}</small>
                    </td>
                    <td>
                      {o.que}
                      {o.parametros.version && <b> {o.parametros.version}</b>}
                    </td>
                    <td>
                      <Pastilla tono={TONO[o.estado]}>{DICHO[o.estado]}</Pastilla>
                    </td>
                    <td>
                      {(o.estado === 'pendiente' || o.estado === 'entregada') && <span className="gira" aria-hidden="true" />}
                      {bilingue(o.detalle) ?? (o.terminada ? '—' : o.estado === 'entregada' ? tx.actualizar.esperandoAvance : '')}
                    </td>
                    <td className="der">
                      {(o.estado === 'pendiente' || o.estado === 'entregada') && puede.actualizar && (
                        <button
                          type="button"
                          className="btn peligro"
                          onClick={() =>
                            void pedir(`/ordenes/${o.id}/cancelar`, {
                              metodo: 'POST',
                            }).then(() => {
                              void ordenes.refetch();
                              alCambiar(tx.actualizar.ordenCancelada);
                            })
                          }
                        >
                          {tx.comun.cancelar}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tabla>
          </>
        )}
      </Consulta>

      {pedirlo && (
        <Modal
          titulo={tx.actualizar.actualizarEste}
          boton={tx.actualizar.pedirBoton}
          alCerrar={() => setPedirlo(false)}
          alEnviar={async () => {
            if (!elegida) return tx.actualizar.elijaVersion;
            await pedir(`/appliances/${applianceId}/actualizar`, {
              metodo: 'POST',
              cuerpo: { version: elegida },
            });
            await ordenes.refetch();
            setPedirlo(false);
            alCambiar(tx.actualizar.pedida);
          }}
        >
          <label className="campo" htmlFor="act-version">
            {tx.comun.version}
            <select id="act-version" className="lista-sel" value={elegida} onChange={(e) => setElegida(e.target.value)}>
              <option value="">{tx.comun.elegir}</option>
              {disponibles.map((v) => (
                <option key={v.numero} value={v.numero}>
                  {v.numero} ({v.canal})
                </option>
              ))}
            </select>
          </label>
          <p className="nota">
            {tx.actualizar.queHace}
          </p>
        </Modal>
      )}

      {editarVentana && (
        <EditarVentana
          actual={ventana}
          alCerrar={() => setEditarVentana(false)}
          alGuardar={async (v) => {
            await pedir(`/appliances/${applianceId}`, {
              metodo: 'PATCH',
              cuerpo: { ventana: v },
            });
            setEditarVentana(false);
            alCambiar(tx.actualizar.ventanaGuardada);
          }}
        />
      )}
    </section>
  );
}

function EditarVentana({
  actual,
  alCerrar,
  alGuardar,
}: {
  actual: VentanaMant | null;
  alCerrar: () => void;
  alGuardar: (v: VentanaMant | null) => Promise<void>;
}) {
  const [dias, setDias] = useState<number[]>(actual?.dias ?? []);
  const [desde, setDesde] = useState(actual?.desde ?? '02:00');
  const [hasta, setHasta] = useState(actual?.hasta ?? '04:00');
  return (
    <Modal
      titulo={tx.actualizar.ventana}
      boton={tx.comun.guardar}
      alCerrar={alCerrar}
      alEnviar={async () => {
        if (desde === hasta) return tx.actualizar.mismaHora;
        await alGuardar({ dias, desde, hasta });
      }}
    >
      <p className="nota">{tx.actualizar.ventanaQue}</p>
      <div className="ventana-dias" role="group" aria-label={tx.actualizar.dias}>
        {DIAS.map((d, i) => (
          <button
            key={d}
            type="button"
            aria-pressed={dias.includes(i)}
            onClick={() => setDias((v) => (v.includes(i) ? v.filter((x) => x !== i) : [...v, i].sort()))}
          >
            {d}
          </button>
        ))}
      </div>
      <div className="ventana-horas">
        <label className="campo" htmlFor="v-desde">
          {tx.actualizar.desde}
          <input id="v-desde" className="entrada" type="time" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </label>
        <label className="campo" htmlFor="v-hasta">
          {tx.actualizar.hasta}
          <input id="v-hasta" className="entrada" type="time" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </label>
        <button type="button" className="btn" onClick={() => void alGuardar(null)}>
          {tx.actualizar.quitarVentana}
        </button>
      </div>
    </Modal>
  );
}
